// RequiredItemRow.jsx
import {
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import moment from 'moment'
import AnDateField from '../../../components/DateTime/DateField'
import ExpiredRow from './ExpiredRow'
import AlmostExpiredRow from './AlmostExpiredRow'
import ReadyRow from './ReadyRow'

const RequiredItemRow = ({
  item,
  replacementRows,
  lastExpiryDate,
  onReplacementDateChange,
  onAddReplacement,
  onUseLastExpiry,
  expiredRows = [],
  expiringSoonRows = [],
  recordedRows = [],
  showRecordedStock = false,
  onRemoveExpired,
  onRemoveExpiringSoon,
  onRemoveRecorded,
}) => {
  const reusableExpiryDate =
    lastExpiryDate || replacementRows.find((row) => row.expiryDate)?.expiryDate
  const reusableExpiryMoment = reusableExpiryDate
    ? moment(reusableExpiryDate)
    : null
  const reusableExpiryLabel = reusableExpiryMoment?.isValid()
    ? moment(reusableExpiryDate).format('DD/MM/YYYY')
    : ''
  const hasExpiredStock = expiredRows.length > 0
  const hasExpiringSoonStock = expiringSoonRows.length > 0
  const isComplete = item.needed === 0 && !hasExpiredStock && !hasExpiringSoonStock
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: isComplete
          ? 'success.light'
          : hasExpiringSoonStock && item.needed === 0
            ? 'warning.light'
            : 'error.light',
        borderLeft: '5px solid',
        borderLeftColor: isComplete
          ? 'success.main'
          : hasExpiringSoonStock && item.needed === 0
            ? 'warning.main'
            : 'error.main',
        bgcolor: isComplete ? 'rgba(0, 150, 76, 0.04)' : 'background.paper',
      }}
    >
      <Grid container spacing={1.5} alignItems="center">
        <Grid item xs={12} lg={4}>
          <Stack gap={0.5}>
            <Typography fontWeight={700}>
              {item.name} {item.size && `(${item.size})`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.noExpiry
                ? 'No expiry date required'
                : 'Expiry date required'}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={3}>
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            <Chip size="small" label={`Required ${item.requiredQty}`} />
            <Chip size="small" label={`Recorded ${item.recordedQty}`} />
            <Chip
              size="small"
              color={
                isComplete
                  ? 'success'
                  : hasExpiringSoonStock && item.needed === 0
                    ? 'warning'
                    : 'error'
              }
              label={
                hasExpiredStock
                  ? 'Expired stock'
                  : isComplete
                    ? 'Complete'
                    : hasExpiringSoonStock && item.needed === 0
                      ? 'Expiring soon'
                    : item.needed > 0
                      ? `${item.needed} needed`
                      : 'Review'
              }
            />
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent={{ sm: 'flex-end' }}
            gap={1}
          >
            <Chip
              size="small"
              color={item.presentQty >= item.requiredQty ? 'success' : 'error'}
              variant="outlined"
              label={`Present ${item.presentQty} of ${item.requiredQty}`}
            />
            {item.recordedQty < item.requiredQty && (
              <Typography variant="caption" color="text.secondary">
                Add replacements for stock not already recorded.
              </Typography>
            )}
          </Stack>
        </Grid>
      </Grid>

      {item.needed > 0 && (
        <Stack gap={1} mt={1.5}>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <EventAvailableIcon color="action" fontSize="small" />
            <Typography variant="caption" color="text.secondary">
              Book in each replacement as it is picked.
            </Typography>
          </Stack>

          {replacementRows.map((entry, index) => (
            <Paper
              key={entry.id}
              variant="outlined"
              sx={{ p: 1, bgcolor: 'background.default' }}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} lg={2}>
                  <Typography variant="caption" color="text.secondary">
                    Replacement {index + 1}
                  </Typography>
                </Grid>
                {!item.noExpiry && (
                  <Grid item xs={12} lg={4}>
                    <AnDateField
                      fullWidth
                      onChange={(newDate) =>
                        onReplacementDateChange(index, newDate)
                      }
                      value={entry.expiryDate || null}
                      format="DD/MM/YYYY"
                      label="Expiry"
                      size="small"
                    />
                  </Grid>
                )}
                {!item.noExpiry && reusableExpiryLabel && !entry.expiryDate && (
                  <Grid item xs={12} lg={2}>
                    <Button
                      variant="contained"
                      color="anPurple"
                      size="small"
                      fullWidth
                      onClick={() => onUseLastExpiry(index, reusableExpiryDate)}
                    >
                      Use {reusableExpiryLabel}
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12} lg={item.noExpiry ? 3 : 2}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    fullWidth
                    startIcon={<AddIcon />}
                    disabled={!item.noExpiry && !entry.expiryDate}
                    onClick={() => onAddReplacement(index)}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      )}

      {(expiredRows.length > 0 || expiringSoonRows.length > 0) && (
        <Stack gap={1} mt={1.5}>
          <Typography variant="caption" color="text.secondary">
            Recorded stock needing attention
          </Typography>
          {expiredRows.map((expiredItem, index) => (
            <ExpiredRow
              key={`expired-${item.key}-${index}`}
              item={expiredItem}
              onRemove={() => onRemoveExpired(expiredItem)}
            />
          ))}
          {expiringSoonRows.map((soonItem, index) => (
            <AlmostExpiredRow
              key={`soon-${item.key}-${index}`}
              item={soonItem}
              onRemove={() => onRemoveExpiringSoon(soonItem)}
            />
          ))}
        </Stack>
      )}

      {showRecordedStock && recordedRows.length > 0 && (
        <Stack gap={1} mt={1.5}>
          <Typography variant="caption" color="text.secondary">
            Recorded stock
          </Typography>
          {recordedRows.map((recordedItem, index) => (
            <ReadyRow
              key={`ready-${item.key}-${index}`}
              item={recordedItem}
              onRemove={() => onRemoveRecorded(recordedItem)}
            />
          ))}
        </Stack>
      )}
    </Paper>
  )
}

export default RequiredItemRow
